import * as skedApiAll from "../_skedApi";
import { GeneratedSkedApiClient } from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";
import { CollectionFile } from "shared/types/files";
import { MediaCollection } from "./types";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();
let legacySkedApiOverride: typeof skedApiAll.skedApi | null = null;

type ListInboxesOperation = paths["/content-inbox/inbox"]["get"];
type GetAssetsByInboxIdOperation =
  paths["/content-inbox/inbox/{inboxId}/assets"]["get"];
type LegacyPaginatedResponse<T> = {
  data: T[];
  pages?: number;
};

export function setMediaLibrarySkedApiInGetCollections(
  customSkedApi: typeof skedApiAll.skedApi
) {
  legacySkedApiOverride = customSkedApi;
}

export async function getCollections(): Promise<MediaCollection[]> {
  if (legacySkedApiOverride) {
    const {
      data: { data },
    } = await legacySkedApiOverride.get(
      `/content-inbox/inbox?_limit=900&_sort=name`
    );
    return data;
  }

  const result = await generatedSkedApiClient.client.GET(
    "/content-inbox/inbox",
    {
      params: {
        query: {
          _limit: 900,
          _sort: "name",
        } as ListInboxesOperation["parameters"]["query"] & {
          _limit: number;
          _sort: string;
        },
      },
    }
  );
  const data = await generatedSkedApiClient.unwrap(result);

  return normalizeListResponse<MediaCollection>(data);
}

export async function getCollectionMedia({
  collectionId,
  page: _page = 1,
  limit: _limit,
  sort: _sort = "-created",

  name,
  tags,
  description,
  sourceName,
  favorite,
  mediaType,
  usage,
}: {
  collectionId: string;
  page?: number;
  sort?: string;
  name?: string;
  description?: string;
  sourceName?: string;
  tags?: string[];
  favorite?: boolean;
  limit: number;
  mediaType?: "image" | "video" | null;
  usage?: "used" | "unused" | null;
}): Promise<{
  list: CollectionFile[];
  pagesCount: number;
}> {
  if (legacySkedApiOverride) {
    const {
      data: { data, pages },
    } = await legacySkedApiOverride.get(
      `/content-inbox/inbox/${collectionId}/assets`,
      {
        params: {
          _page,
          _sort,
          _limit,
          name,
          tags,
          description,
          sourceName,
          favorite,
          mediaType,
          usage,
        },
      }
    );

    return { list: data, pagesCount: pages };
  }

  const query = {
    _page,
    _sort,
    _limit,
    name,
    "tags[]": tags,
    description,
    sourceName,
    favorite,
    mediaType,
    usage,
  } as GetAssetsByInboxIdOperation["parameters"]["query"] & {
    _page: number;
    _sort: string;
    _limit: number;
    description?: string;
    sourceName?: string;
    favorite?: boolean;
    mediaType?: "image" | "video";
    usage: "used" | "unused" | null;
  };

  const result = await generatedSkedApiClient.client.GET(
    "/content-inbox/inbox/{inboxId}/assets",
    {
      params: {
        path: { inboxId: collectionId },
        query,
      },
    }
  );
  const data = await generatedSkedApiClient.unwrap(result);
  const normalized = normalizePaginatedResponse<CollectionFile>(data);

  return { list: normalized.data, pagesCount: normalized.pages ?? 0 };
}

export async function getCollectionTags(
  collectionId: string
): Promise<string[]> {
  if (legacySkedApiOverride) {
    const { data } = await legacySkedApiOverride.get<
      Array<{ count: number; tag: string }>
    >(`/content-inbox/inbox/${collectionId}/tags`);
    return data.map((item) => item.tag);
  }

  const result = await generatedSkedApiClient.client.GET(
    "/content-inbox/inbox/{inboxId}/tags",
    {
      params: {
        path: { inboxId: collectionId },
      },
    }
  );
  const data = await generatedSkedApiClient.unwrap(result);

  return data.map((item) => item.tag).filter((tag) => isString(tag));
}

function normalizeListResponse<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }

  return normalizePaginatedResponse<T>(data).data;
}

function normalizePaginatedResponse<T>(
  data: unknown
): LegacyPaginatedResponse<T> {
  if (Array.isArray(data)) {
    return { data: data as T[] };
  }

  if (isLegacyPaginatedResponse<T>(data)) {
    return data;
  }

  return { data: [] };
}

function isLegacyPaginatedResponse<T>(
  data: unknown
): data is LegacyPaginatedResponse<T> {
  return typeof data === "object" && data !== null && "data" in data;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}
